/**
 * Connection tests: the result view (steps, what was checked, where from), the recent-tests list
 * and the dialogs that run a test. EPP: connection, encryption and greeting, optionally a sign-in
 * with the stored account. REST and VNNIC services: reachability (no credentials, data or
 * business request is sent) and, where the provider has one, a sign-in check ("Kiểm tra đăng
 * nhập", API_LOGIN): the stored credentials for a token and one read-only call.
 */

import { CircleCheck, CircleDashed, CircleX, Play, ShieldCheck } from 'lucide-react';
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import type {
  ConnectionProtocol,
  ConnectionTest,
  ConnectionTestBody,
  EnvironmentDetail,
  IntegrationEnvironment,
  IntegrationProvider,
  PresentedCertificate,
  ProviderSettingDetail,
  TestStep,
  TestSummary,
} from '../../api/areas/integrations';
import { Badge } from '../../components/Badge';
import { Callout } from '../../components/blocks';
import { CopyText } from '../../components/States';
import { useWrite } from '../../components/Write';
import { formatCount, formatDateTime } from '../../lib/format';
import { RootCaDialog } from './connection/dialogs';
import { ChangeDialog, EnvironmentBanner, IntegrationError, serviceLabel, StaffName, tx, useIntegrationContext, When } from './shared';

export function TestOutcomeBadge({ outcome }: { outcome: 'OK' | 'FAILED' }) {
  const { t } = useTranslation('integrations');
  return <Badge tone={outcome === 'OK' ? 'ok' : 'crit'}>{t(`test.outcome.${outcome}`)}</Badge>;
}

/** "Passed · 2 hours ago" line for summaries. */
export function LastTest({ test }: { test: TestSummary | null }) {
  const { t } = useTranslation('integrations');
  if (!test) return <span className="text-xs text-muted-foreground">{t('test.never')}</span>;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5 text-xs">
      <TestOutcomeBadge outcome={test.outcome} />
      <span className="text-muted-foreground">{t(`test.kind.${test.test_kind}`)}</span>
      <When at={test.finished_at} className="text-muted-foreground" />
    </span>
  );
}

const STEP_ICON = { OK: CircleCheck, FAILED: CircleX, SKIPPED: CircleDashed } as const;

function StepDetail({ step, target }: { step: TestStep; target?: { connectionId: string; connectionName: string; environment: IntegrationEnvironment } }) {
  const { t } = useTranslation('integrations');
  const d = step.detail;
  const parts: ReactNode[] = [];
  if (step.error_code) parts.push(<span key="err" className="text-critical">{tx(`test.error.${step.error_code}`, step.error_code)}</span>);
  if (d.addresses?.length) parts.push(<span key="addr">{d.addresses.join(', ')}</span>);
  if (d.via_proxy) parts.push(<span key="proxy">{t('test.viaProxy')}</span>);
  if (d.address) parts.push(<span key="tcp">{d.address}</span>);
  if (d.tls_version) parts.push(<span key="tls">{d.tls_version}</span>);
  if (d.server_certificate) {
    parts.push(
      <span key="cert">
        {t('test.serverCertificate', { subject: d.server_certificate.subject, days: d.server_certificate.days_remaining })}
      </span>,
    );
  }
  if (d.tls_problem) parts.push(<span key="tp" className="text-critical">{t(`test.tlsProblem.${d.tls_problem}`)}</span>);
  if (d.client_certificate_version !== undefined && d.client_certificate_version !== null) parts.push(<span key="cc">{t('test.clientCertificate', { version: d.client_certificate_version })}</span>);
  if (d.server_id) parts.push(<span key="sv">{t('test.serverId', { id: d.server_id })}</span>);
  if (d.object_uris) parts.push(<span key="obj">{t('test.services', { objects: d.object_uris.length, extensions: d.extension_uris?.length ?? 0 })}</span>);
  if (d.missing_extensions?.length) parts.push(<span key="miss" className="text-warning">{t('test.missingExtensions', { list: d.missing_extensions.join(', ') })}</span>);
  if (d.result_code !== undefined) parts.push(<span key="rc">{t('test.resultCode', { code: d.result_code, message: d.message ?? '' })}</span>);
  else if (d.message) parts.push(<span key="msg" className="break-all font-mono">{d.message}</span>);
  if (d.http_status !== undefined) parts.push(<span key="http">{t('test.httpStatus', { status: d.http_status })}</span>);
  if (d.interpretation) parts.push(<span key="int">{t(`test.interpretation.${d.interpretation}`)}</span>);
  if (d.endpoint) parts.push(<span key="ep" className="font-mono">{t('test.endpoint', { endpoint: d.endpoint })}</span>);
  if (d.provider_code) parts.push(<span key="pc">{t('test.providerCode', { code: d.provider_code })}</span>);
  if (d.balances?.length) parts.push(<span key="bal">{t('test.balances', { list: d.balances.map((b) => `${b.amount} ${b.currency}`).join(', ') })}</span>);
  if (d.item_count !== undefined) parts.push(<span key="ic">{t('test.itemCount', { count: d.item_count })}</span>);
  if (d.egress_addresses?.length) parts.push(<span key="eg" className="text-warning">{t('test.egress', { list: d.egress_addresses.join(', ') })}</span>);
  if (!parts.length && !d.server_chain?.length) return null;
  return (
    <div className="space-y-2">
      {parts.length ? <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">{parts}</div> : null}
      {d.server_chain?.length ? <ServerChain chain={d.server_chain} target={target} /> : null}
    </div>
  );
}

/** The chain an untrusted server presented, with the CA it names, to compare and trust. */
function ServerChain({ chain, target }: { chain: PresentedCertificate[]; target?: { connectionId: string; connectionName: string; environment: IntegrationEnvironment } }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [trusting, setTrusting] = useState(false);
  const ca = chain.filter((c, i) => i > 0 || c.self_signed);
  const caPem = ca.map((c) => c.pem.trim()).join('\n');
  return (
    <div className="space-y-2 rounded-md border border-dashed p-3 text-xs">
      <p className="font-medium text-foreground">{t('test.serverChain.title')}</p>
      <ol className="grid gap-1.5">
        {chain.map((c, i) => (
          <li key={c.sha256} className="grid gap-0.5">
            <span className="font-medium text-foreground">
              {i === 0 ? t('test.serverChain.leaf') : c.self_signed ? t('rootCa.root') : t('rootCa.intermediate')}: <span className="break-all">{c.subject}</span>
            </span>
            <span className="text-muted-foreground break-all">
              {t('certificates.issuer')}: {c.issuer} · {t('certificates.validity')}: {formatDateTime(c.not_before)} → {formatDateTime(c.not_after)}
            </span>
            <span className="text-muted-foreground">
              SHA-256: <CopyText value={c.sha256} display={c.sha256.match(/.{1,2}/g)?.join(':').toUpperCase() ?? c.sha256} />
            </span>
          </li>
        ))}
      </ol>
      {ca.length === 0 ? (
        <p className="text-muted-foreground">{t('test.serverChain.noCa')}</p>
      ) : target ? (
        <>
          <p className="text-muted-foreground">{t('test.serverChain.compare')}</p>
          <Button type="button" size="xs" variant="outline" onClick={() => setTrusting(true)}>
            <ShieldCheck /> {t('test.serverChain.trust')}
          </Button>
          <RootCaDialog open={trusting} onOpenChange={setTrusting} connectionId={target.connectionId} connectionName={target.connectionName} env={ctx.integration_environment} initialPem={caPem} onDone={() => undefined} />
        </>
      ) : null}
    </div>
  );
}

/** Full result of one test: every step, what was used, and where it ran from. */
export function TestResultView({ test }: { test: ConnectionTest }) {
  const { t } = useTranslation('integrations');
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <TestOutcomeBadge outcome={test.outcome} />
        <span className="font-medium">{t(`test.kind.${test.test_kind}`)}</span>
        <span className="text-muted-foreground">
          <When at={test.finished_at} /> · {formatCount(test.duration_ms)} ms
        </span>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {test.tested_version !== null ? <span>{t('test.testedVersion', { version: test.tested_version })}</span> : null}
        {test.credential_version !== null ? <span>{t('test.credentialVersion', { version: test.credential_version })}</span> : null}
        {test.certificate_version !== null ? <span>{t('test.certificateVersion', { version: test.certificate_version })}</span> : null}
        <span className="inline-flex items-center gap-1">
          {t('test.by')} <StaffName staff={test.requested_by} />
        </span>
      </div>
      <ol className="grid gap-1.5">
        {test.steps.map((s, i) => {
          const Icon = STEP_ICON[s.status];
          return (
            <li key={`${s.step}-${i}`} className="flex gap-2 rounded-md border px-3 py-2">
              <Icon className={cn('mt-0.5 size-4 shrink-0', s.status === 'OK' ? 'text-success' : s.status === 'FAILED' ? 'text-critical' : 'text-muted-foreground')} aria-hidden="true" />
              <div className="min-w-0 flex-1 space-y-0.5">
                <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                  <span className="font-medium">{t(`test.step.${s.step}`)}</span>
                  <span className="text-xs text-muted-foreground">
                    {t(`test.stepStatus.${s.status}`)}
                    {s.duration_ms !== null ? ` · ${formatCount(s.duration_ms)} ms` : ''}
                  </span>
                </div>
                <StepDetail step={s} target={test.target.kind === 'REGISTRY_ENVIRONMENT' ? { connectionId: test.target.connection.id, connectionName: test.target.connection.name, environment: test.target.environment } : undefined} />
              </div>
            </li>
          );
        })}
      </ol>
      <p className="text-xs text-muted-foreground">
        <span title={`${test.probe.service} · ${test.probe.instance}`}>{t('test.probe', { service: serviceLabel(test.probe.service) })}</span>
        {test.test_kind === 'HTTPS_REACHABILITY' ? ` ${t('test.reachabilityOnly')}` : test.test_kind === 'API_LOGIN' ? ` ${t('test.loginNote')}` : ''}
      </p>
    </div>
  );
}

/** Recent tests, newest first; each one expands to its steps. */
export function TestList({ tests }: { tests: readonly ConnectionTest[] }) {
  const { t } = useTranslation('integrations');
  const [open, setOpen] = useState<string | null>(null);
  if (!tests.length) return <p className="text-sm text-muted-foreground">{t('test.none')}</p>;
  return (
    <ul className="grid gap-2">
      {tests.map((x) => (
        <li key={x.id} className="rounded-md border">
          <button
            type="button"
            className="flex w-full flex-wrap items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/50"
            aria-expanded={open === x.id}
            onClick={() => setOpen(open === x.id ? null : x.id)}
          >
            <TestOutcomeBadge outcome={x.outcome} />
            <span className="font-medium">{t(`test.kind.${x.test_kind}`)}</span>
            <span className="text-xs text-muted-foreground">
              <When at={x.finished_at} /> · {x.requested_by.display_name}
            </span>
            <span className="ml-auto text-xs text-primary">{open === x.id ? t('test.hide') : t('test.show')}</span>
          </button>
          {open === x.id ? (
            <div className="border-t p-3">
              <TestResultView test={x} />
            </div>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

type Mode = ConnectionTestBody['mode'];

function ModeOption({ mode, selected, onSelect, title, body, disabled = false, note }: { mode: Mode; selected: boolean; onSelect: (m: Mode) => void; title: string; body: string; disabled?: boolean; note?: string }) {
  const id = useId();
  return (
    <div className={cn('flex gap-3 rounded-lg border p-3 text-sm', selected && 'border-primary bg-accent/40', disabled && 'opacity-60')}>
      <input id={id} type="radio" name="test-mode" className="mt-1" checked={selected} disabled={disabled} onChange={() => onSelect(mode)} aria-describedby={`${id}-d`} />
      <div className="space-y-0.5">
        <label htmlFor={id} className={cn('block font-medium', disabled ? 'cursor-not-allowed' : 'cursor-pointer')}>
          {title}
        </label>
        <p id={`${id}-d`} className="text-xs text-muted-foreground">
          {body}
          {note ? <span className="mt-0.5 block font-medium text-foreground/80">{note}</span> : null}
        </p>
      </div>
    </div>
  );
}

/** Runs a test of one connection environment. The sign-in mode is labelled as using the stored account. */
export function ConnectionTestDialog({
  open,
  onOpenChange,
  connectionId,
  protocol,
  adapter,
  env,
  detail,
  onTested,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  protocol: ConnectionProtocol;
  /** The connection's adapter (REST: decides whether the sign-in check is offered). */
  adapter?: string;
  env: IntegrationEnvironment;
  detail: EnvironmentDetail;
  onTested: () => void;
}) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('test.dialogTitle', { name: detail.connection.name })} description={t('test.dialogDescription')} wide>
      <ConnectionTestForm connectionId={connectionId} protocol={protocol} adapter={adapter} env={env} detail={detail} onTested={onTested} onClose={() => onOpenChange(false)} />
    </ChangeDialog>
  );
}

function ConnectionTestForm({ connectionId, protocol, adapter, env, detail, onTested, onClose }: { connectionId: string; protocol: ConnectionProtocol; adapter?: string; env: IntegrationEnvironment; detail: EnvironmentDetail; onTested: () => void; onClose: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  // REST: a sign-in check exists only for adapters that declare one (e.g. WebNIC).
  const restLogin = protocol === 'REST' && ctx.rest_adapters.some((a) => a.key === adapter && a.login_test);
  const [mode, setMode] = useState<Mode>(protocol === 'EPP' ? 'HANDSHAKE' : 'REACHABILITY');
  const [target, setTarget] = useState<'active' | 'pending'>(detail.active ? 'active' : 'pending');
  const [credential, setCredential] = useState<'active' | 'pending'>('active');
  const write = useWrite((body: ConnectionTestBody, opts) => api.testConnection(connectionId, body, opts));
  const pendingCredential = detail.credentials.find((c) => c.status === 'PENDING');
  const version = target === 'pending' ? detail.pending : detail.active;
  // A sign-in sends the stored account, so the server runs it only against the address in force;
  // a proposed version is checked with the handshake alone.
  const loginAllowed = !!detail.active && target === 'active';
  const fallback: Mode = protocol === 'EPP' ? 'HANDSHAKE' : 'REACHABILITY';
  const effectiveMode: Mode = mode === 'LOGIN' && (!loginAllowed || (protocol === 'REST' && !restLogin)) ? fallback : mode;
  const run = async () => {
    const res = await write.run({
      mode: effectiveMode,
      version_id: version?.id ?? null,
      credential_version: effectiveMode === 'LOGIN' && credential === 'pending' && pendingCredential ? pendingCredential.version : null,
      certificate_version: null,
    });
    if (res) onTested();
  };
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} message={t('test.bannerNote')} />
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">{t('test.modeLabel')}</legend>
        {protocol === 'EPP' ? (
          <>
            <ModeOption mode="HANDSHAKE" selected={effectiveMode === 'HANDSHAKE'} onSelect={setMode} title={t('test.mode.HANDSHAKE')} body={t('test.modeHint.HANDSHAKE')} />
            <ModeOption
              mode="LOGIN"
              selected={effectiveMode === 'LOGIN'}
              onSelect={setMode}
              disabled={!loginAllowed}
              note={loginAllowed ? undefined : t('test.loginOnlyInForce')}
              title={t('test.mode.LOGIN')}
              body={t('test.modeHint.LOGIN', { perHour: ctx.test_limits.epp_login_per_hour, seconds: ctx.test_limits.epp_login_interval_seconds })}
            />
          </>
        ) : (
          <>
            <ModeOption mode="REACHABILITY" selected={effectiveMode === 'REACHABILITY'} onSelect={setMode} title={t('test.mode.REACHABILITY')} body={t('test.modeHint.REACHABILITY')} />
            {restLogin ? (
              <ModeOption
                mode="LOGIN"
                selected={effectiveMode === 'LOGIN'}
                onSelect={setMode}
                disabled={!loginAllowed}
                note={loginAllowed ? undefined : t('test.apiLoginOnlyInForce')}
                title={t('test.mode.API_LOGIN')}
                body={t('test.modeHint.API_LOGIN', { call: t(`test.loginCall.${adapter === 'webnic' ? 'webnic' : 'other'}`) })}
              />
            ) : null}
          </>
        )}
      </fieldset>
      {detail.pending ? (
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">{t('test.versionLabel')}</legend>
          <div className="flex flex-wrap gap-2">
            {detail.active ? (
              <Button type="button" size="sm" variant={target === 'active' ? 'default' : 'outline'} onClick={() => setTarget('active')}>
                {t('test.activeVersion', { version: detail.active.version })}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant={target === 'pending' ? 'default' : 'outline'} onClick={() => setTarget('pending')}>
              {t('test.pendingVersion', { version: detail.pending.version })}
            </Button>
          </div>
        </fieldset>
      ) : null}
      {effectiveMode === 'LOGIN' && pendingCredential ? (
        <fieldset className="grid gap-2">
          <legend className="mb-1 text-sm font-medium">{t('test.credentialLabel')}</legend>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant={credential === 'active' ? 'default' : 'outline'} onClick={() => setCredential('active')}>
              {t('test.credentialActive')}
            </Button>
            <Button type="button" size="sm" variant={credential === 'pending' ? 'default' : 'outline'} onClick={() => setCredential('pending')}>
              {t('test.credentialPending', { version: pendingCredential.version })}
            </Button>
          </div>
        </fieldset>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('test.limits', { seconds: ctx.test_limits.per_target_interval_seconds })}</p>
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      {write.state.phase === 'done' ? (
        <div className="rounded-lg border p-3">
          <TestResultView test={write.state.result} />
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
        <Button type="button" onClick={() => void run()} disabled={write.busy || !version}>
          <Play /> {write.busy ? t('test.running') : write.state.phase === 'done' ? t('test.runAgain') : t('test.run')}
        </Button>
      </DialogFooter>
    </div>
  );
}

/** Tests a VNNIC service address: reachability (never sends credentials or personal data) or,
 * for services with `loginTest`, a sign-in check with the stored credentials (one read). */
export function ProviderTestDialog({
  open,
  onOpenChange,
  provider,
  loginTest = false,
  env,
  detail,
  onTested,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider: IntegrationProvider;
  loginTest?: boolean;
  env: IntegrationEnvironment;
  detail: ProviderSettingDetail;
  onTested: () => void;
}) {
  const { t } = useTranslation('integrations');
  return (
    <ChangeDialog open={open} onOpenChange={onOpenChange} title={t('test.providerDialogTitle', { name: t(`provider.name.${provider}`) })} description={t('test.dialogDescription')} wide>
      <ProviderTestForm provider={provider} loginTest={loginTest} env={env} detail={detail} onTested={onTested} onClose={() => onOpenChange(false)} />
    </ChangeDialog>
  );
}

type ProviderMode = 'REACHABILITY' | 'LOGIN';

function ProviderTestForm({ provider, loginTest, env, detail, onTested, onClose }: { provider: IntegrationProvider; loginTest: boolean; env: IntegrationEnvironment; detail: ProviderSettingDetail; onTested: () => void; onClose: () => void }) {
  const { t } = useTranslation('integrations');
  const ctx = useIntegrationContext();
  const [target, setTarget] = useState<'active' | 'pending'>(detail.active ? 'active' : 'pending');
  const [mode, setMode] = useState<ProviderMode>('REACHABILITY');
  const write = useWrite((body: { mode: ProviderMode; versionId: string | null }, opts) => api.testProviderSetting(provider, { mode: body.mode, version_id: body.versionId }, opts));
  const version = target === 'pending' ? detail.pending : detail.active;
  // The stored credentials go only to the address in force.
  const loginAllowed = !!detail.active?.base_url && version?.base_url === detail.active.base_url;
  const effectiveMode: ProviderMode = mode === 'LOGIN' && (!loginTest || !loginAllowed) ? 'REACHABILITY' : mode;
  const run = async () => {
    const res = await write.run({ mode: effectiveMode, versionId: target === 'pending' ? (detail.pending?.id ?? null) : null });
    if (res) onTested();
  };
  const call = provider === 'VNNIC_REGISTRAR_API' || provider === 'VNNIC_HSTM' ? provider : 'other';
  return (
    <div className="space-y-4">
      <EnvironmentBanner env={env} message={t('test.bannerNote')} />
      <fieldset className="grid gap-2">
        <legend className="mb-1 text-sm font-medium">{t('test.modeLabel')}</legend>
        <ModeOption mode="REACHABILITY" selected={effectiveMode === 'REACHABILITY'} onSelect={(m) => setMode(m as ProviderMode)} title={t('test.mode.REACHABILITY')} body={t('test.modeHint.REACHABILITY')} />
        {loginTest ? (
          <ModeOption
            mode="LOGIN"
            selected={effectiveMode === 'LOGIN'}
            onSelect={(m) => setMode(m as ProviderMode)}
            disabled={!loginAllowed}
            note={loginAllowed ? undefined : t('test.apiLoginOnlyInForce')}
            title={t('test.mode.API_LOGIN')}
            body={t('test.modeHint.API_LOGIN', { call: t(`test.loginCall.${call}`) })}
          />
        ) : null}
      </fieldset>
      {detail.pending ? (
        <div className="flex flex-wrap gap-2">
          {detail.active ? (
            <Button type="button" size="sm" variant={target === 'active' ? 'default' : 'outline'} onClick={() => setTarget('active')}>
              {t('test.activeVersion', { version: detail.active.version })}
            </Button>
          ) : null}
          <Button type="button" size="sm" variant={target === 'pending' ? 'default' : 'outline'} onClick={() => setTarget('pending')}>
            {t('test.pendingVersion', { version: detail.pending.version })}
          </Button>
        </div>
      ) : null}
      {version?.base_url ? (
        <p className="text-sm">
          {t('test.address')}: <code className="break-all rounded bg-muted px-1 text-xs">{version.base_url}</code>
        </p>
      ) : (
        <Callout tone="warn">{t('test.noAddress')}</Callout>
      )}
      <p className="text-xs text-muted-foreground">{t('test.limits', { seconds: ctx.test_limits.per_target_interval_seconds })}</p>
      {write.state.phase === 'error' ? <IntegrationError error={write.state.error} /> : null}
      {write.state.phase === 'done' ? (
        <div className="rounded-lg border p-3">
          <TestResultView test={write.state.result} />
        </div>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
        <Button type="button" onClick={() => void run()} disabled={write.busy || !version?.base_url}>
          <Play /> {write.busy ? t('test.running') : write.state.phase === 'done' ? t('test.runAgain') : t('test.run')}
        </Button>
      </DialogFooter>
    </div>
  );
}
